import React, { useState, useRef, useEffect } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  Box,
  Typography,
  IconButton
} from '@mui/material';
import CloseIcon from '@mui/icons-material/Close';
import CloudUploadIcon from '@mui/icons-material/CloudUpload';
import DeleteIcon from '@mui/icons-material/Delete';
import EventsApi from '../../../api/eventsApi/eventsApi';

interface CoverPhotoModalProps {
  open: boolean;
  onClose: () => void;
  pinId: string;
  userId: string;
  onPhotoUploaded?: () => void;
}

const CoverPhotoModal: React.FC<CoverPhotoModalProps> = ({
  open,
  onClose,
  pinId,
  userId,
  onPhotoUploaded
}) => {
  const [selectedFile, setSelectedFile] = useState<File | null>(null);
  const [previewUrl, setPreviewUrl] = useState<string | null>(null);
  const [isUploading, setIsUploading] = useState(false);
  const [existingCoverPhoto, setExistingCoverPhoto] = useState<string | null>(null);
  const fileInputRef = useRef<HTMLInputElement>(null);
  const CANVAS_WIDTH = 1200; // Standart genişlik
  const CANVAS_HEIGHT = 630; // 16:9 oranı için uygun yükseklik

  useEffect(() => {
    const fetchCoverPhoto = async () => {
      if (pinId) {
        try {
          const pinInfo = await EventsApi.getPinFullReport(pinId);
          const coverPhoto = pinInfo.pinInfo.fileAttachments?.find(file => 
            file.fileName.startsWith('cover') && 
            (file.fileName.endsWith('.jpg') || file.fileName.endsWith('.jpeg') || file.fileName.endsWith('.png'))
          );

          if (coverPhoto) {
            const photoBlob = await EventsApi.getFileAttachment(coverPhoto.id);
            const photoUrl = URL.createObjectURL(photoBlob);
            setExistingCoverPhoto(photoUrl);
            setPreviewUrl(photoUrl);
          }
        } catch (error) {
          console.error('Error fetching cover photo:', error);
        }
      }
    };

    if (open) {
      fetchCoverPhoto();
    }

    return () => {
      // Cleanup
      if (existingCoverPhoto) {
        URL.revokeObjectURL(existingCoverPhoto);
      }
    };
  }, [open, pinId]);

  const resizeImage = (file: File): Promise<Blob> => {
    return new Promise((resolve, reject) => {
      const img = new Image();
      img.src = URL.createObjectURL(file);
      
      img.onload = () => {
        // Orijinal resim boyutlarını logla
        console.log('Orijinal Resim Boyutları:', {
          genişlik: img.width,
          yükseklik: img.height,
          dosyaBoyutu: (file.size / 1024 / 1024).toFixed(2) + ' MB',
          aspectRatio: (img.width / img.height).toFixed(2)
        });

        // Canvas boyutlarını logla
        console.log('Canvas Boyutları:', {
          genişlik: CANVAS_WIDTH,
          yükseklik: CANVAS_HEIGHT,
          aspectRatio: (CANVAS_WIDTH / CANVAS_HEIGHT).toFixed(2)
        });

        // Canvas oluştur
        const canvas = document.createElement('canvas');
        canvas.width = CANVAS_WIDTH;
        canvas.height = CANVAS_HEIGHT;
        const ctx = canvas.getContext('2d');
        
        if (!ctx) {
          reject(new Error('Canvas context not available'));
          return;
        }

        // Resim boyutlarını hesapla
        let sourceWidth = img.width;
        let sourceHeight = img.height;
        let destX = 0;
        let destY = 0;
        let destWidth = CANVAS_WIDTH;
        let destHeight = CANVAS_HEIGHT;

        // Aspect ratio'yu koru
        const targetRatio = CANVAS_WIDTH / CANVAS_HEIGHT;
        const imageRatio = sourceWidth / sourceHeight;

        if (imageRatio > targetRatio) {
          // Resim daha geniş, yüksekliğe göre ölçekle
          destWidth = CANVAS_HEIGHT * imageRatio;
          destX = -(destWidth - CANVAS_WIDTH) / 2;
        } else {
          // Resim daha uzun, genişliğe göre ölçekle
          destHeight = CANVAS_WIDTH / imageRatio;
          destY = -(destHeight - CANVAS_HEIGHT) / 2;
        }

        // Hesaplanan boyutları logla
        console.log('Yerleştirme Boyutları:', {
          x: destX,
          y: destY,
          genişlik: destWidth,
          yükseklik: destHeight
        });

        // Arka planı beyaz yap
        ctx.fillStyle = '#FFFFFF';
        ctx.fillRect(0, 0, CANVAS_WIDTH, CANVAS_HEIGHT);

        // Resmi çiz
        ctx.drawImage(
          img,
          0, 0, sourceWidth, sourceHeight,  // Kaynak koordinatları
          destX, destY, destWidth, destHeight // Hedef koordinatları
        );

        // Canvas'ı blob'a çevir
        canvas.toBlob(
          (blob) => {
            if (blob) {
              // İşlenmiş resim boyutunu logla
              console.log('İşlenmiş Resim Boyutu:', {
                dosyaBoyutu: (blob.size / 1024 / 1024).toFixed(2) + ' MB'
              });
              resolve(blob);
            } else {
              reject(new Error('Canvas to Blob conversion failed'));
            }
          },
          'image/jpeg',
          0.9  // Kalite (0-1 arası)
        );

        // Temizlik
        URL.revokeObjectURL(img.src);
      };

      img.onerror = () => {
        reject(new Error('Image loading failed'));
        URL.revokeObjectURL(img.src);
      };
    });
  };

  const handleFileSelect = async (event: React.ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files?.[0];
    if (file) {
      try {
        // Önce dosyayı önizleme için göster
        const reader = new FileReader();
        reader.onloadend = () => {
          setPreviewUrl(reader.result as string);
        };
        reader.readAsDataURL(file);

        // Resized versiyonunu sakla
        const resizedBlob = await resizeImage(file);
        const resizedFile = new File([resizedBlob], file.name, {
          type: 'image/jpeg',
          lastModified: Date.now(),
        });
        setSelectedFile(resizedFile);
      } catch (error) {
        console.error('Error processing image:', error);
      }
    }
  };

  const handleUpload = async () => {
    if (!selectedFile || !pinId || !userId) return;

    try {
      setIsUploading(true);
      // Dosya uzantısını al
      const fileExtension = selectedFile.name.split('.').pop();
      // Yeni dosya adı oluştur - "cover" ile başlayan format
      const newFileName = `cover${pinId}.${fileExtension}`;
      
      // Yeni File objesi oluştur
      const renamedFile = new File([selectedFile], newFileName, {
        type: selectedFile.type,
        lastModified: selectedFile.lastModified,
      });

      const formData = new FormData();
      formData.append('Files', renamedFile);
      formData.append('Id', pinId);
      formData.append('UserId', userId);

      await EventsApi.updatePin(pinId, formData);
      onPhotoUploaded?.();
      onClose();
    } catch (error) {
      console.error('Error uploading cover photo:', error);
    } finally {
      setIsUploading(false);
    }
  };

  const handleClickUpload = () => {
    fileInputRef.current?.click();
  };

  const handleRemoveFile = () => {
    setSelectedFile(null);
    setPreviewUrl(null);
    if (fileInputRef.current) {
      fileInputRef.current.value = '';
    }
  };

  return (
    <Dialog 
      open={open} 
      onClose={onClose}
      maxWidth="sm"
      fullWidth
    >
      <DialogTitle>
        <Box display="flex" justifyContent="space-between" alignItems="center">
          <Typography variant="h6">Kapak Fotoğrafı Ekle</Typography>
          <IconButton onClick={onClose} size="small">
            <CloseIcon />
          </IconButton>
        </Box>
      </DialogTitle>
      <DialogContent>
        <Box
          sx={{
            display: 'flex',
            flexDirection: 'column',
            alignItems: 'center',
            gap: 2,
            py: 2
          }}
        >
          <input
            type="file"
            ref={fileInputRef}
            onChange={handleFileSelect}
            accept="image/*"
            style={{ display: 'none' }}
          />
          
          {(previewUrl || existingCoverPhoto) ? (
            <Box
              sx={{
                width: '100%',
                position: 'relative',
                '&::before': {
                  content: '""',
                  display: 'block',
                  paddingTop: '52.5%', // 630/1200 = 0.525 (16:9 oranı)
                },
                borderRadius: 1,
                overflow: 'hidden',
              }}
            >
              <img
                src={previewUrl || existingCoverPhoto || ''}
                alt="Preview"
                style={{
                  position: 'absolute',
                  top: 0,
                  left: 0,
                  width: '100%',
                  height: '100%',
                  objectFit: 'cover'
                }}
              />
              <IconButton
                onClick={handleRemoveFile}
                sx={{
                  position: 'absolute',
                  top: 8,
                  right: 8,
                  bgcolor: 'rgba(255, 255, 255, 0.8)',
                  '&:hover': {
                    bgcolor: 'rgba(255, 255, 255, 0.9)',
                  }
                }}
              >
                <DeleteIcon sx={{ color: '#d32f2f' }} />
              </IconButton>
            </Box>
          ) : (
            <Box
              onClick={handleClickUpload}
              sx={{
                width: '100%',
                position: 'relative',
                '&::before': {
                  content: '""',
                  display: 'block',
                  paddingTop: '52.5%', // 630/1200 = 0.525 (16:9 oranı)
                },
                border: '2px dashed #b3c2d1',
                borderRadius: 1,
                display: 'flex',
                flexDirection: 'column',
                alignItems: 'center',
                justifyContent: 'center',
                cursor: 'pointer',
                bgcolor: '#f3f6fa',
                '&:hover': {
                  bgcolor: '#e8eef5'
                }
              }}
            >
              <Box
                sx={{
                  position: 'absolute',
                  top: 0,
                  left: 0,
                  width: '100%',
                  height: '100%',
                  display: 'flex',
                  flexDirection: 'column',
                  alignItems: 'center',
                  justifyContent: 'center',
                }}
              >
                <CloudUploadIcon sx={{ fontSize: 48, color: '#094151', mb: 1 }} />
                <Typography variant="body1" color="textSecondary">
                  Fotoğraf seçmek için tıklayın
                </Typography>
              </Box>
            </Box>
          )}
        </Box>
      </DialogContent>
      <DialogActions sx={{ p: 2 }}>
        <Button 
          onClick={onClose}
          variant="contained"
          sx={{
            backgroundColor: '#fff',
            color: '#094151',
            border: '1.5px solid #094151',
            fontWeight: 600,
            borderRadius: '8px',
            boxShadow: 'none',
            '&:hover': {
              backgroundColor: '#d0e7ec',
              color: '#094151',
            }
          }}
        >
          İptal
        </Button>
        <Button
          variant="contained"
          onClick={handleUpload}
          disabled={!selectedFile || isUploading}
          sx={{
            backgroundColor: '#094151',
            color: '#fff',
            fontWeight: 600,
            borderRadius: '8px',
            boxShadow: 'none',
            '&:hover': {
              backgroundColor: '#022631',
              color: '#fff',
            }
          }}
        >
          {isUploading ? 'Yükleniyor...' : 'Yükle'}
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default CoverPhotoModal; 